import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { apiClient } from '@/lib/api';
import { useAuthStore } from '@/store/authStore';
import Button from '@components/ui/Button';
import { Input } from '@components/ui/Input';
import { Label } from '@components/ui/Label';
import PasswordInput from '@components/forms/PasswordInput';
import PhoneInput from '@components/forms/PhoneInput';
import { Alert, AlertDescription } from '@components/ui/Alert';
import { RiUserLine, RiPhoneLine, RiBuildingLine, RiArrowRightLine, RiAlertLine } from '@remixicon/react';
import { toast } from 'sonner';
import { decodeData, encodeData } from '@/utils';
import VerifySplash from '@components/auth/VerifySplash';
import CrossDeviceRecovery from '@components/auth/CrossDeviceRecovery';

export default function TeamRegistration() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [inviteData, setInviteData] = useState(() => decodeData(searchParams.get('d')));
  const [currentStep, setCurrentStep] = useState('register');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  // Gate the whole render until the session watcher has decided
  // whether we can auto-finalise. Without this, the register form
  // flashes for a beat before we redirect to /dashboard on the
  // post-verify landing — which reads to the user as "the page
  // didn't know I already did all this."
  const [resolvingSession, setResolvingSession] = useState(true);
  const [finalizingMessage, setFinalizingMessage] = useState('Verifying your invitation…');
  // Set when the verify link's ?code= is present but the PKCE
  // exchange never completes — meaning email is verified server-side
  // but the code_verifier is missing (invitee opened the link on a
  // different device / browser than the one they signed up on).
  const [crossDeviceRecovery, setCrossDeviceRecovery] = useState(false);
  // Set when Supabase redirected back with error_code=otp_expired.
  // Happens when an email link scanner (spam filter, Gmail/Outlook
  // preview, corporate proxy) silently followed the verify link
  // before the user did, consuming the one-time token. Also fires
  // if the user genuinely waited past the token's expiry window.
  const [expiredLink, setExpiredLink] = useState(false);
  const [resendingExpired, setResendingExpired] = useState(false);

  // Registration state
  const [email, setEmail] = useState(inviteData.email || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [success, setSuccess] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownRef = useRef(null);

  // Profile state
  const [formData, setFormData] = useState({
    firstName: inviteData.firstName || '',
    lastName: inviteData.lastName || '',
    phone: '',
    countryCode: '+1',
    role: inviteData.role || 'user',
    orgId: inviteData.org_id || '',
  });

  useEffect(() => {
    return () => clearInterval(cooldownRef.current);
  }, []);

  // Re-sync the form when inviteData gets hydrated late (i.e. we
  // recovered it from user_metadata because the URL param was
  // missing). Only overwrite fields that are still empty so we never
  // clobber something the user typed.
  useEffect(() => {
    if (!inviteData) return;
    if (!email && inviteData.email) setEmail(inviteData.email);
    setFormData(prev => ({
      ...prev,
      firstName: prev.firstName || inviteData.firstName || '',
      lastName: prev.lastName || inviteData.lastName || '',
      role: (prev.role && prev.role !== 'user') ? prev.role : (inviteData.role || 'user'),
      orgId: prev.orgId || inviteData.org_id || '',
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteData]);

  // Resume from an existing Supabase session — covers three cases:
  //  1. User clicks the invite email verification link → Supabase's
  //     detectSessionInUrl consumes the token and lands here with a
  //     session already established. Without this watcher we sat on
  //     Step 1 forever, so the BE `POST /users/register` never ran.
  //  2. Supabase's redirect-URL whitelist rejected our
  //     emailRedirectTo and dumped the user at `/` — they sign in
  //     manually, the resume helper reads user_metadata.invite and
  //     bounces them back here; we should now finalise on the spot
  //     instead of asking them to re-enter password.
  //  3. Existing BE profile → they've already been through this flow;
  //     send them to /dashboard so we don't try to double-register.
  //
  // When every field the register endpoint requires is already known
  // (firstName / lastName / role / org_id all come from the admin's
  // invite payload), auto-submit and skip the "Complete Your Profile"
  // step. That step only asked the user to click a button — the phone
  // number is optional and can be added later from Account Settings.
  useEffect(() => {
    let mounted = true;
    // When the watcher runs after the initial mount (e.g. SIGNED_IN
    // fires late), re-open the loading UI so the register form
    // doesn't blink before we auto-register.
    const beginResolving = (msg) => {
      if (!mounted) return;
      setFinalizingMessage(msg);
      setResolvingSession(true);
    };
    const revealRegisterForm = () => {
      if (!mounted) return;
      setResolvingSession(false);
    };

    const resumeFromSession = async (session) => {
      if (!mounted) return;
      if (!session?.user) {
        revealRegisterForm();
        return;
      }
      beginResolving('Verifying your invitation…');
      // Cross-account guard: the current Supabase session might
      // belong to a DIFFERENT user (e.g. someone signs out via our
      // Zustand store which — until recently — didn't call
      // supabase.auth.signOut(), then opens an invite link intended
      // for another person). Never resume that session on someone
      // else's invite; sign it out and let the invitee start fresh
      // on Step 1. Comparison is case-insensitive because email
      // clients normalise case inconsistently.
      const inviteEmail = String(inviteData?.email || '').toLowerCase();
      const sessionEmail = String(session.user.email || '').toLowerCase();
      if (inviteEmail && sessionEmail && inviteEmail !== sessionEmail) {
        console.warn('TeamRegistration: session email does not match invite — signing out');
        await supabase.auth.signOut().catch(() => {});
        revealRegisterForm();
        return;
      }
      // If the URL is missing ?d= (e.g. Supabase's redirect-URL
      // whitelist stripped it), fall back to the invite payload we
      // stashed on user_metadata at signup so we still have
      // firstName / lastName / org.
      let effectiveInvite = inviteData;
      if (!effectiveInvite?.email && session.user.user_metadata?.invite) {
        const decoded = decodeData(session.user.user_metadata.invite);
        if (decoded && Object.keys(decoded).length > 0) {
          effectiveInvite = decoded;
          setInviteData(decoded);
        }
      }
      // Pre-compute the finalized name so the reactivation body below
      // can adopt the invite's values without re-plumbing later.
      const metaFirstNamePre = session.user.user_metadata?.first_name;
      const metaLastNamePre = session.user.user_metadata?.last_name;
      const finalFirstNamePre = metaFirstNamePre || effectiveInvite?.firstName;
      const finalLastNamePre = metaLastNamePre || effectiveInvite?.lastName;

      // Guard against double-registration on repeat visits. This is
      // ALSO the reactivation entry point — an invitee whose profile
      // already exists in an ARCHIVED / INACTIVE / ONBOARDING_* state
      // (or as a race-winner ACTIVE row) should be upserted here
      // instead of hitting a UNIQUE(user_id) collision on registerUser.
      const existing = await apiClient
        .getUserByUserId(session.user.id)
        .catch(() => null);
      if (!mounted) return;
      if (existing?.userprofile_id) {
        // Cross-org guard: if the existing profile belongs to a
        // DIFFERENT org than the invite, refuse to silently migrate
        // them. Auto-moving a user across orgs would be a
        // permission-elevation footgun. Surface the error via the
        // register form's error banner + reveal the form so the user
        // sees the message and can bail.
        if (
          effectiveInvite?.org_id
          && existing.org_id
          && existing.org_id !== effectiveInvite.org_id
        ) {
          setError(
            'You already belong to another organization. Contact support to change organizations.',
          );
          setResolvingSession(false);
          revealRegisterForm();
          return;
        }
        // Reactivate anything that isn't already ACTIVE-with-is_active.
        // Covers ARCHIVED, INACTIVE, ONBOARDING_STARTED,
        // ONBOARDING_COMPLETED, and race-winner rows that came in with
        // a stale flag. When the invite carries a role/name, adopt
        // them; otherwise leave the existing values in place.
        const existingStatus = (existing.status || '').toUpperCase();
        const needsReactivation = existingStatus !== 'ACTIVE' || existing.is_active !== true;
        if (needsReactivation) {
          const body = { status: 'ACTIVE', is_active: true };
          if (effectiveInvite?.role) body.role = effectiveInvite.role;
          if (finalFirstNamePre) body.first_name = finalFirstNamePre;
          if (finalLastNamePre) body.last_name = finalLastNamePre;
          try {
            beginResolving('Reactivating your account…');
            await apiClient.updateUser(existing.userprofile_id, body);
          } catch (patchErr) {
            console.warn('TeamRegistration: reactivation PATCH failed', patchErr);
            // Fall through to the dashboard anyway — the auth session
            // is valid; the user will hit whatever error state the
            // dashboard surfaces (e.g. inactive-account banner) and
            // support can PATCH manually. Better than dead-ending here.
          }
        }
        await useAuthStore.getState().fetchUserProfile();
        if (!mounted) return;
        beginResolving('Taking you to your dashboard…');
        navigate('/dashboard');
        return;
      }
      // Prefer the freshest source of the invitee's name. Priority
      // order:
      //   1. Supabase user_metadata.first_name / last_name — these
      //      are written at signUp time and carry any Step 1 edits.
      //   2. effectiveInvite (URL ?d= param or user_metadata.invite).
      // Both #1 and #2 come from the same signUp call today, but
      // #1 is the canonical location Supabase persists. Falls back
      // to the invite payload for safety.
      const metaFirstName = session.user.user_metadata?.first_name;
      const metaLastName = session.user.user_metadata?.last_name;
      const finalFirstName = metaFirstName || effectiveInvite?.firstName;
      const finalLastName = metaLastName || effectiveInvite?.lastName;
      // Auto-register if every required field is present.
      const canAutoRegister = !!finalFirstName
        && !!finalLastName
        && !!effectiveInvite?.role
        && !!effectiveInvite?.org_id;
      if (canAutoRegister) {
        beginResolving(`Setting up your account, ${finalFirstName}…`);
        try {
          await apiClient.registerUser({
            user_id: session.user.id,
            email: session.user.email,
            org_id: effectiveInvite.org_id,
            role: effectiveInvite.role,
            first_name: finalFirstName,
            last_name: finalLastName,
            phone: '',
            status: 'ACTIVE',
            is_active: 'TRUE',
          });
          await useAuthStore.getState().fetchUserProfile();
          if (!mounted) return;
          setFinalizingMessage('All set — taking you to your dashboard…');
          navigate('/dashboard');
          return;
        } catch (err) {
          // registerUser failed — most commonly because a race winner
          // (parallel tab OR a pre-existing archived/inactive row for
          // the same user_id) already occupies the UNIQUE slot. Look
          // it up and treat it the same way the pre-flight `existing`
          // branch above does: apply the cross-org guard, reactivate
          // if the row is stale, then land on the dashboard.
          console.warn('TeamRegistration: auto-register failed — checking for existing row', err);
          const raceWinner = await apiClient
            .getUserByUserId(session.user.id)
            .catch(() => null);
          if (!mounted) return;
          if (raceWinner?.userprofile_id) {
            if (
              effectiveInvite?.org_id
              && raceWinner.org_id
              && raceWinner.org_id !== effectiveInvite.org_id
            ) {
              setError(
                'You already belong to another organization. Contact support to change organizations.',
              );
              setResolvingSession(false);
              revealRegisterForm();
              return;
            }
            const winnerStatus = (raceWinner.status || '').toUpperCase();
            const winnerNeedsReactivation = winnerStatus !== 'ACTIVE' || raceWinner.is_active !== true;
            if (winnerNeedsReactivation) {
              const body = { status: 'ACTIVE', is_active: true };
              if (effectiveInvite?.role) body.role = effectiveInvite.role;
              if (finalFirstName) body.first_name = finalFirstName;
              if (finalLastName) body.last_name = finalLastName;
              try {
                await apiClient.updateUser(raceWinner.userprofile_id, body);
              } catch (patchErr) {
                console.warn('TeamRegistration: race-winner reactivation PATCH failed', patchErr);
              }
            }
            await useAuthStore.getState().fetchUserProfile();
            setFinalizingMessage('All set — taking you to your dashboard…');
            navigate('/dashboard');
            return;
          }
          // Genuine failure — fall through so the user can retry
          // manually on the profile step.
        }
      }
      setUser(session.user);
      setCurrentStep('profile');
      setSuccess(false);
      revealRegisterForm();
    };
    // Supabase reports auth-flow errors both in the query string
    // and (redundantly) in the URL fragment. Check both — email link
    // scanners can strip one or the other.
    const queryParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const errorCode = queryParams.get('error_code') || hashParams.get('error_code');
    if (errorCode === 'otp_expired' || errorCode === 'access_denied') {
      // Show the expired-link recovery UI with a Resend button
      // instead of dumping the invitee into the blank register form.
      setExpiredLink(true);
      setResolvingSession(false);
      return () => {
        mounted = false;
      };
    }
    // Same pattern as EmailConfirmation.jsx: if the URL carries a
    // ?code= (verify-link landing) but no session materialises
    // within a grace period, the PKCE code_verifier is missing
    // — that means the invitee opened this link on a different
    // device / browser than the one they signed up on. Show the
    // sign-in recovery state so they can re-authenticate here.
    const hasCodeParam = queryParams.has('code');
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) return resumeFromSession(data.session);
      if (!hasCodeParam) return resumeFromSession(null);
      setFinalizingMessage('Finalising your verification…');
      setTimeout(async () => {
        if (!mounted) return;
        const { data: retry } = await supabase.auth.getSession();
        if (retry.session) return resumeFromSession(retry.session);
        // Server-side verified, client-side session never established.
        setCrossDeviceRecovery(true);
      }, 2000);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN') resumeFromSession(session);
    });
    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, [navigate]);

  const startCooldown = (seconds) => {
    setResendCooldown(seconds);
    clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(cooldownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSignUp = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setError(null);
    setSuccess(false);

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    if (!acceptTerms) {
      setError("You must accept the terms and conditions");
      return;
    }

    setLoading(true);

    try {
      // Push the invite payload into Supabase user_metadata so the
      // context survives even if the user opens the email link on a
      // different device (device B has no ?d= URL param — the resume
      // helper reads the invite from session.user.user_metadata.invite
      // and routes them back to /register/team with the same payload).
      //
      // Use the same buildInviteRedirectUrl helper as the resends
      // so every outbound verification link carries the current
      // form values (post-edit names).
      const emailRedirectTo = buildInviteRedirectUrl();
      const inviteEncoded = encodeData({
        ...(inviteData || {}),
        firstName: formData.firstName,
        lastName: formData.lastName,
      });
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo,
          data: {
            invite: inviteEncoded,
            first_name: formData.firstName,
            last_name: formData.lastName,
          },
        },
      })

      if (error) throw error;

      if (data.user) {
        if (data.user.identities && data.user.identities.length === 0) {
          // Invitee already started signup and closed the browser
          // before clicking the verification email. Supabase does
          // NOT send a fresh confirmation email on a repeat signUp —
          // so we must explicitly resend, otherwise the invitee sits
          // on the Verify Email screen waiting for a mail that never
          // arrives. Fire-and-forget: the resend call may 429 if
          // rate-limited, but we still show the verify screen so
          // they can click the Resend button once the cooldown ends.
          supabase.auth.resend({
            type: 'signup',
            email,
            options: { emailRedirectTo },
          }).catch((err) => {
            console.warn('TeamRegistration: repeat-signup resend failed', err);
          });
          setSuccess(true);
          startCooldown(60);
        } else if (data.user.confirmed_at) {
          setUser(data.user);
          setCurrentStep('profile');
        } else {
          setSuccess(true);
          startCooldown(60);
        }
      };

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Rebuild the invite redirect URL with the freshest form values.
  // The URL bar still holds the original `?d=` param from the admin's
  // invite; the invitee may have edited firstName / lastName on
  // Step 1. Every outbound resend must use this so verification
  // emails always carry the latest edits — otherwise a resend
  // silently reverts to the admin's original names.
  const buildInviteRedirectUrl = () => {
    const encoded = encodeData({
      ...(inviteData || {}),
      firstName: formData.firstName || inviteData?.firstName || '',
      lastName: formData.lastName || inviteData?.lastName || '',
    });
    return `${window.location.origin}/register/team?d=${encodeURIComponent(encoded)}`;
  };

  const handleResendVerification = async () => {
    setLoading(true);
    setError(null);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
        options: {
          emailRedirectTo: buildInviteRedirectUrl(),
        },
      });
      if (error) throw error;
      startCooldown(60);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Resend when landing on the expired-link recovery screen. Same
  // Supabase call as handleResendVerification but doesn't require
  // the register form to have been submitted first (that path uses
  // the local `password` state; here the account already exists so
  // we just need the email address from the invite payload).
  const handleResendFromExpiredLink = async () => {
    const targetEmail = email || inviteData?.email;
    if (!targetEmail) {
      toast.error('Missing invite email. Please contact your admin.');
      return;
    }
    setResendingExpired(true);
    setError(null);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: targetEmail,
        options: {
          emailRedirectTo: buildInviteRedirectUrl(),
        },
      });
      if (error) throw error;
      toast.success('New verification email sent — check your inbox.', {
        position: 'bottom-right',
      });
      // Flip into the standard Verify Email screen with cooldown so
      // the invitee can wait for the new email without seeing the
      // stale expired-link UI.
      if (!email && inviteData?.email) setEmail(inviteData.email);
      setExpiredLink(false);
      setSuccess(true);
      startCooldown(60);
    } catch (err) {
      toast.error(err.message || 'Failed to resend verification email.', {
        position: 'bottom-right',
      });
    } finally {
      setResendingExpired(false);
    }
  };

  const handleNextAfterVerification = async () => {
    setLoading(true);
    try {
      // Path 1: session already established (email verified in
      // another tab, cross-tab localStorage sync fired). The auth
      // watcher above will pick this up on its own — nudge it by
      // showing the splash so the user sees feedback.
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setFinalizingMessage('Verifying your invitation…');
        setResolvingSession(true);
        return;
      }
      // Path 2: no session yet — try password sign-in. If the email
      // is verified server-side (via a different device), this
      // succeeds and triggers the SIGNED_IN watcher, which runs
      // auto-register and navigates to /dashboard.
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) {
        toast.warning('Please confirm your email address before continuing.');
        return;
      }
      // Show the splash while the auth watcher takes over.
      setFinalizingMessage('Verifying your invitation…');
      setResolvingSession(true);
    } catch {
      toast.warning('Please confirm your email address before continuing.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleProfileSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setLoading(true);

    try {
      // Belt-and-braces: if this page reopens with a session but
      // `user` state was never populated (e.g. hydration timing on the
      // email-link landing), read the session directly.
      const authUser = user || (await supabase.auth.getSession()).data.session?.user;
      if (!authUser) {
        toast.error('Session expired. Please sign in again.');
        navigate('/');
        return;
      }

      // Skip register if the BE profile already exists — user could
      // land here twice (email verify + manual sign-in) and we don't
      // want the second visit to 409.
      const existing = await apiClient.getUserByUserId(authUser.id).catch(() => null);
      if (existing?.userprofile_id) {
        await useAuthStore.getState().fetchUserProfile();
        navigate('/dashboard');
        return;
      }

      // Register user in the gtj schema. Invited users go directly
      // to ACTIVE — the admin who invited them IS the approval action
      // (no manual review needed like org-owner signup). This flips
      // the resume helper's next-login routing to /dashboard right
      // away and avoids the 403 loop the previous default status
      // caused (no status set → helper thought they were mid-
      // onboarding → they'd land on /onboarding which is the wrong
      // flow for invitees).
      await apiClient.registerUser({
        user_id: authUser.id,
        email: authUser.email,
        org_id: formData.orgId,
        role: formData.role,
        first_name: formData.firstName,
        last_name: formData.lastName,
        phone: formData.phone ? `${formData.countryCode}${formData.phone}` : '',
        status: 'ACTIVE',
        is_active: 'TRUE',
      });

      await useAuthStore.getState().fetchUserProfile();

      navigate('/dashboard');
    } catch (err) {
      console.error('Profile setup error:', err);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Expired-link recovery — the OTP token was already consumed
  // (usually by an email-scanner preview) or genuinely expired.
  // Show a resend action instead of dumping the invitee into a
  // blank register form.
  if (expiredLink) {
    return (
      <div className="flex flex-col items-center justify-center py-4">
        <div className="max-w-md w-full flex flex-col flex-1">
          <div className="text-center lg:text-left mb-6">
            <h2 className="text-3xl font-bold text-primary-500">
              This link is no longer valid
            </h2>
            <p className="text-gray-600 mt-2">
              Verification links expire after a few minutes and can only be
              used once. Sometimes email security scanners follow the link
              before you get a chance to. Send a new one below and open it
              right away.
            </p>
          </div>
          <div className="flex flex-col items-center justify-center flex-1 py-6">
            <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center mb-6">
              <RiAlertLine className="w-8 h-8 text-amber-500" />
            </div>
            <p className="text-sm text-gray-700 text-center mb-6">
              We'll send a fresh verification link to{' '}
              <span className="font-medium">{inviteData?.email || email}</span>.
            </p>
            <Button
              type="button"
              className="w-full !font-normal bg-primary-500 hover:bg-primary-600 rounded-full"
              onClick={handleResendFromExpiredLink}
              disabled={resendingExpired}
            >
              {resendingExpired ? 'Sending…' : 'Send new verification email'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Cross-device recovery — email was verified server-side but the
  // PKCE code_verifier is missing on this device.
  if (crossDeviceRecovery) {
    return (
      <CrossDeviceRecovery
        subtitle="Looks like you opened this link on a different device or browser than where you signed up. Sign in with the password you just created to finish accepting your invitation."
      />
    );
  }

  // Auto-finalise splash — shown while the session watcher checks
  // whether we can skip the register/profile forms and register the
  // invitee directly. Keeps the invitee from ever seeing the
  // password form flash on the post-verify landing.
  if (resolvingSession) {
    const companyLabel = inviteData?.company;
    const firstName = inviteData?.firstName;
    const done = finalizingMessage.startsWith('All set');
    const title = companyLabel
      ? `Welcome to ${companyLabel}${firstName ? `, ${firstName}` : ''}`
      : 'Setting things up';
    return (
      <VerifySplash
        title={title}
        subtitle="Hang tight — we're finishing your invitation. No action needed."
        message={finalizingMessage}
        variant={done ? 'success' : 'loading'}
      />
    );
  }

  return (
    <div className="flex flex-col items-center justify-center py-4">
      <div className="max-w-md w-full flex flex-col flex-1">
        {/* Header */}
        <div className="text-center lg:text-left mb-6">
          <h2 className="text-3xl font-bold text-primary-500">
            {currentStep === 'register'
              ? (success ? "Verify Email" : `You've been invited to join ${inviteData.company}`)
              : "Complete Your Profile"}
          </h2>
          {currentStep === 'profile' && (
            <p className="text-gray-600 mt-2">
              Please provide some additional information to complete your registration.
            </p>
          )}
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="destructive" className="border-red-200 bg-red-50 mb-3">
            <RiAlertLine className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="flex-1">
          {/* Registration Step */}
          {currentStep === 'register' && (
            <>
              {/* Verify Email Screen */}
              {success && (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">
                    We've sent an email to <span className="font-bold text-gray-900">{email}</span>
                  </p>
                  <p className="text-sm text-gray-600">
                    Recovery link will expire in 2 minutes.
                  </p>
                  <p className="text-sm text-gray-600">
                    Open the email and click on the link provided to verify your email address - come back here and tap next below when you're done.
                  </p>
                  <button
                    type="button"
                    onClick={handleResendVerification}
                    disabled={loading || resendCooldown > 0}
                    className="text-sm text-primary-500 hover:underline font-medium disabled:opacity-50 disabled:no-underline"
                  >
                    {resendCooldown > 0
                      ? `Resend Verification Email (${resendCooldown}s)`
                      : 'Resend Verification Email'}
                  </button>
                </div>
              )}

              {/* Sign Up Form */}
              {!success && (
                <div className="space-y-4">
                  <p className="text-sm">You are signing up as <span className='font-medium'>{inviteData.email}</span></p>
                  <form onSubmit={handleSignUp} className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label htmlFor="firstName">First Name <span className="text-[#0C3E71]"> *</span></Label>
                        <Input
                          id="firstName"
                          name="firstName"
                          type="text"
                          placeholder="First name"
                          value={formData.firstName}
                          onChange={handleChange}
                          required
                          disabled={loading}
                          className="h-11 !bg-white !border-2 !border-border-light !text-slate-500 !rounded-lg"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="lastName">Last Name <span className="text-[#0C3E71]"> *</span></Label>
                        <Input
                          id="lastName"
                          name="lastName"
                          type="text"
                          placeholder="Last name"
                          value={formData.lastName}
                          onChange={handleChange}
                          required
                          disabled={loading}
                          className="h-11 !bg-white !border-2 !border-border-light !text-slate-500 !rounded-lg"
                        />
                      </div>
                    </div>

                    <PasswordInput
                      id="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      required={true}
                      disabled={loading}
                      label="Password"
                      showLabel={true}
                      showValidation={true}
                    />

                    <PasswordInput
                      id="confirmPassword"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                      required={true}
                      disabled={loading}
                      label="Confirm Password"
                      showLabel={true}
                      showValidation={false}
                    />

                    <PhoneInput
                      countryCode={formData.countryCode}
                      phone={formData.phone}
                      onCountryCodeChange={(code) => setFormData((prev) => ({ ...prev, countryCode: code }))}
                      onPhoneChange={(phone) => setFormData((prev) => ({ ...prev, phone }))}
                      required
                    />

                    <div className="flex items-start space-x-2">
                      <input
                        type="checkbox"
                        id="terms"
                        checked={acceptTerms}
                        onChange={(e) => setAcceptTerms(e.target.checked)}
                        disabled={loading}
                        className="mt-0.5 w-4 h-4 text-primary-500 border-gray-300 rounded focus:ring-primary-500"
                      />
                      <label
                        htmlFor="terms"
                        className="text-sm text-slate-600 leading-tight cursor-pointer"
                      >
                        I accept the{" "}
                        <a href="../docs/TRUSTJET TERMS OF SERVICE v1.pdf" target="_blank" className="text-[#0C3E71] hover:underline">
                          Terms and Conditions
                        </a>{" "}
                        and{" "}
                        <a href="../docs/TRUSTJET PRIVACY POLICY v1.pdf" target="_blank" className="text-[#0C3E71] hover:underline">
                          Privacy Policy
                        </a>
                      </label>
                    </div>
                  </form>
                </div>
              )}
            </>
          )}

          {/* Profile Step */}
          {currentStep === 'profile' && (
            <form onSubmit={handleProfileSubmit} className="space-y-6">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name <span className="text-red-500">*</span></Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                      <RiUserLine size={16} />
                    </span>
                    <Input
                      id="firstName"
                      name="firstName"
                      type="text"
                      placeholder="Enter your first name"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      disabled={loading}
                      className="h-11 pl-10 bg-white border-2 border-gray-200 text-gray-700 rounded-lg focus:none"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name <span className="text-red-500">*</span></Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                      <RiUserLine size={16} />
                    </span>
                    <Input
                      id="lastName"
                      name="lastName"
                      type="text"
                      placeholder="Enter your last name"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      disabled={loading}
                      className="h-11 pl-10 bg-white border-2 border-gray-200 text-gray-700 rounded-lg focus:outline-none"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                    <RiPhoneLine size={16} />
                  </span>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="Enter your phone number (optional)"
                    value={formData.phone}
                    onChange={handleChange}
                    disabled={loading}
                    className="h-11 pl-10 bg-white border-2 border-gray-200 text-gray-700 rounded-lg focus:outline-none"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="orgId">Organization <span className="text-red-500">*</span></Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 z-10">
                    <RiBuildingLine size={16} />
                  </span>
                  {/*
                    Show the preset org from the invite payload directly —
                    the invitee has no permission to `GET /organizations`
                    before their BE profile is created, so listing them
                    would 403 and lock the page. The org_id is already
                    in the invite payload and non-editable here.
                  */}
                  <Input
                    id="orgId"
                    name="orgId"
                    type="text"
                    value={inviteData.company || ''}
                    disabled
                    className="h-11 pl-10 bg-white border-2 border-gray-200 text-gray-700 rounded-lg focus:outline-none disabled:opacity-70"
                  />
                </div>
              </div>
            </form>
          )}
        </div>

        {/* Bottom Buttons */}
        <div className="flex space-x-4 mt-auto pt-6">
          {currentStep === 'register' && (
            <Button
              type="button"
              className="flex-1 !font-normal bg-primary-500 hover:bg-primary-600 disabled:bg-[#F7F7F7] disabled:border disabled:border-border-light disabled:text-gray-400"
              disabled={loading}
              onClick={success ? handleNextAfterVerification : handleSignUp}
            >
              {success ? "Next" : "Create account"}
            </Button>
          )}

          {currentStep === 'profile' && (
            <Button
              type="button"
              className="flex-1 w-full !font-normal bg-primary-500 hover:bg-primary-600 disabled:bg-[#F7F7F7] disabled:border disabled:border-border-light disabled:text-gray-400"
              disabled={loading || !formData.firstName || !formData.lastName || !formData.orgId}
              onClick={handleProfileSubmit}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  Continue
                  <RiArrowRightLine className="ml-2 h-4 w-4" />
                </div>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
